import hoistStatics from 'hoist-non-react-statics';
import { useEffect } from 'react';
import { useTapPay } from 'react-native-tappay';

import { isWebPlatform } from '../../boot/utils';
import { translate } from '../../i18n';
import Config from '../../utils/configs';
import { getIsTW } from '../../utils/countryInfo';
import * as routeUtils from '../../utils/routes/utils';

type Resolve = (value: void | PromiseLike<void>) => void;

const merchantData = {
  merchantName: 'Verybuy Fashion',
  merchantIdentifier: 'merchant.com.verybuy.verybuy',
  countryCode: 'TW',
  currencyCode: 'TWD',
};

export default WrappedComponent => {
  const {
    REACT_APP_TAPPAY_APP_ID,
    REACT_APP_TAPPAY_APP_ID_GC,
    REACT_APP_TAPPAY_APP_ID_KEY,
    REACT_APP_TAPPAY_APP_ID_KEY_GC,
    REACT_APP_TAPPAY_SERVER_TYPE,
  } = Config;

  let _SDKPromiseResolver: Resolve;
  const _SDKPromise = new Promise((resolve, _reject) => {
    _SDKPromiseResolver = resolve;
  });

  const WithTapPay = props => {
    const [isLoadedSuccess, TapPay] = useTapPay({
      appId: getIsTW()
        ? +REACT_APP_TAPPAY_APP_ID!
        : +REACT_APP_TAPPAY_APP_ID_GC!,
      appKey: getIsTW()
        ? REACT_APP_TAPPAY_APP_ID_KEY!
        : REACT_APP_TAPPAY_APP_ID_KEY_GC!,
      env: REACT_APP_TAPPAY_SERVER_TYPE!,
    });

    useEffect(() => {
      if (isLoadedSuccess) {
        // 不確定為何 isLoadSuccess 之後會無法處理 payment，但 delay 一下就可以了
        setTimeout(() => {
          _SDKPromiseResolver();
        }, 150);
      }
    }, [isLoadedSuccess]);

    const webSetupApplePay = ({ totalPrice }) => {
      const cartData = [
        {
          itemName: translate('total'),
          price: totalPrice,
        },
      ];

      return _SDKPromise
        .then(() => TapPay.webSetupApplePay(merchantData, cartData))
        .catch(_e => {
          // console.log('TapPay SDK is not loaded correctly', _e);
        });
    };

    const isApplePayAvailable = () => {
      if (isWebPlatform) {
        return (
          _SDKPromise
            // @ts-ignore
            .then(() => !!window.ApplePaySession)
            .catch(_e => {
              // console.log('TapPay SDK is not loaded correctly', _e);
            })
        );
      } else {
        return TapPay.isApplePayAvailable();
      }
    };

    const getApplePayPrime = ({ totalPrice }) => {
      const cartData = [
        {
          itemName: translate('total'),
          price: totalPrice,
        },
      ];

      return TapPay.getApplePayPrime(merchantData, cartData);
    };

    const getLinePayPrime = ({ transactionID, scene }) => {
      return new Promise((resolve, reject) => {
        TapPay.getLinePayPrime(
          `${Config.REACT_APP_SCHEME_URL}${routeUtils.compileToPath(
            scene,
            { transactionID },
            true
          )}`
        )
          .then(result => resolve(result))
          .catch(error => reject(error));
      });
    };

    const handleLinePayRedirect = async (event): Promise<void> => {
      const url = event?.url;

      TapPay.handleLinePayURL(url).then(Promise.resolve).catch(Promise.reject);
    };

    const TapPayProps = {
      TapPay,
      hasTapPayError: !isLoadedSuccess,
      isTapPayLoadedSuccess: isLoadedSuccess,
      getLinePayPrime,
      isLinePayAvailable: TapPay.isLinePayAvailable,
      handleLinePayRedirect,
      getApplePayPrime,
      isApplePayAvailable,
      webSetupApplePay,
    };

    return <WrappedComponent testID="withTapPay" {...props} {...TapPayProps} />;
  };

  return hoistStatics(WithTapPay, WrappedComponent);
};
